import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import { virtualTradingService } from '../services/virtualTradingService'
import { VirtualPortfolio, VirtualHolding } from '../types/investment'

interface UseVirtualPortfolioReturn {
  portfolio: VirtualPortfolio | null
  holdings: VirtualHolding[]
  loading: boolean
  error: string | null
  refreshPortfolio: () => Promise<void>
  refreshHoldings: () => Promise<void>
  totalValue: number
  totalGainLoss: number
  totalGainLossPercent: number
}

export const useVirtualPortfolio = (): UseVirtualPortfolioReturn => {
  const { user } = useAuth()
  const [portfolio, setPortfolio] = useState<VirtualPortfolio | null>(null)
  const [holdings, setHoldings] = useState<VirtualHolding[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPortfolio = useCallback(async () => {
    if (!user) return

    try {
      setError(null)
      const portfolioData = await virtualTradingService.getPortfolio(user.id)
      setPortfolio(portfolioData)
    } catch (err) {
      console.error('Error fetching portfolio:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch portfolio')
    }
  }, [user])

  const fetchHoldings = useCallback(async () => {
    if (!user) return

    try {
      setError(null)
      const holdingsData = await virtualTradingService.getHoldings(user.id)
      setHoldings(holdingsData)
    } catch (err) {
      console.error('Error fetching holdings:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch holdings')
    }
  }, [user])

  const refreshPortfolio = useCallback(async () => {
    await fetchPortfolio()
  }, [fetchPortfolio])

  const refreshHoldings = useCallback(async () => {
    await fetchHoldings()
  }, [fetchHoldings])

  // Calculate derived values
  const totalValue = portfolio ? portfolio.currentCash + holdings.reduce((sum, h) => sum + h.marketValue, 0) : 0
  const totalGainLoss = portfolio ? totalValue - portfolio.startingBalance : 0
  const totalGainLossPercent = portfolio && portfolio.startingBalance > 0 ? (totalGainLoss / portfolio.startingBalance) * 100 : 0

  // Initial data fetch
  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchPortfolio(), fetchHoldings()])
      setLoading(false)
    }

    loadData()
  }, [user, fetchPortfolio, fetchHoldings])

  // Auto-refresh holdings every 30 seconds
  useEffect(() => {
    if (!user || holdings.length === 0) return

    const interval = setInterval(() => {
      fetchHoldings()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [user, holdings.length, fetchHoldings])

  return {
    portfolio,
    holdings,
    loading,
    error,
    refreshPortfolio,
    refreshHoldings,
    totalValue,
    totalGainLoss,
    totalGainLossPercent
  }
}
